import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Star, Map, Filter, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || 'all');
  const [viewMode, setViewMode] = useState('packages');
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = searchParams.get('q');
        const filter = searchParams.get('filter') || 'all';
        setActiveFilter(filter);
        setSearchQuery(q || '');

        if (q) {
          const res = await axios.post(`${API}/search`, { query: q });
          setDestinations(res.data.destinations);
          setPackages(res.data.packages);
        } else {
          let catQuery = '';
          if (filter === 'domestic' || filter === 'international') catQuery = `?category=${filter}`;

          const [dRes, pRes] = await Promise.all([
            axios.get(`${API}/destinations${catQuery}`),
            axios.get(`${API}/packages${filter === 'domestic' || filter === 'international' ? '' : ''}`),
          ]);
          setDestinations(dRes.data);
          let filteredPkgs = pRes.data;
          if (filter === 'domestic') filteredPkgs = pRes.data.filter(p => ['Goa', 'Kashmir', 'Rajasthan'].includes(p.destination));
          else if (filter === 'international') filteredPkgs = pRes.data.filter(p => !['Goa', 'Kashmir', 'Rajasthan'].includes(p.destination));
          else if (filter === 'bestseller') filteredPkgs = pRes.data.filter(p => p.rating >= 4.7);
          setPackages(filteredPkgs);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const setFilter = (f) => {
    setSearchQuery('');
    setSearchParams(f === 'all' ? {} : { filter: f });
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const filters = [
    { k: 'all', l: 'All' },
    { k: 'domestic', l: 'Domestic' },
    { k: 'international', l: 'International' },
    { k: 'bestseller', l: 'Best Sellers' },
  ];

  return (
    <div data-testid="explore-page" className="min-h-screen bg-white pt-24">
      {/* Header */}
      <div className="bg-navy py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Explore Destinations</h1>
          <p className="font-body text-base text-gray-400 mb-8">Find your perfect getaway from our curated collection</p>

          {/* Search */}
          <form onSubmit={handleSearch} data-testid="explore-search" className="flex items-center gap-2 max-w-2xl">
            <div className="flex-1 flex items-center glass rounded-full px-4 py-2">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search destinations or packages..."
                data-testid="explore-search-input"
                className="w-full bg-transparent border-none outline-none px-3 py-2 font-body text-sm text-navy placeholder:text-gray-400"
              />
              {searchQuery && (
                <button type="button" onClick={clearSearch} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-4 h-4 text-gray-500" /></button>
              )}
            </div>
            <button type="submit" data-testid="explore-search-btn" className="bg-gold hover:bg-gold-dark text-white rounded-full px-6 py-3 font-body text-sm font-medium transition-all">Search</button>
          </form>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-gray-400" />
          {filters.map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}
              className={`px-5 py-2 rounded-full font-body text-sm font-medium transition-all ${activeFilter === f.k ? 'bg-gold text-white' : 'bg-navy-50 text-navy hover:bg-gray-200'}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        {/* Destinations */}
        {destinations.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-bold text-navy mb-6">Destinations ({destinations.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {destinations.map(dest => (
                <Link to={`/explore?q=${dest.name}`} key={dest.id} data-testid={`explore-dest-${dest.id}`}
                  className="dest-card rounded-3xl overflow-hidden h-52 relative group cursor-pointer">
                  <img src={dest.image} alt={dest.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 z-[2] flex flex-col justify-end p-5">
                    <h3 className="font-heading text-xl font-bold text-white">{dest.name}</h3>
                    <p className="font-body text-sm text-white/80">{dest.country} &bull; From Rs.{dest.starting_price.toLocaleString('en-IN')}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Packages */}
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy mb-6">Packages ({packages.length})</h2>
          {packages.length === 0 ? (
            <div className="text-center py-20">
              <Map className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="font-body text-gray-400">No packages found. Try a different search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map(pkg => (
                <div key={pkg.id} data-testid={`explore-pkg-${pkg.id}`} className="pkg-card bg-white rounded-3xl overflow-hidden border border-gray-100">
                  <div className="relative h-48">
                    <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-3 left-3 bg-navy/80 backdrop-blur-sm text-white text-xs font-body px-3 py-1 rounded-full capitalize">{pkg.category}</div>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-xs font-body px-3 py-1 rounded-full flex items-center gap-1 text-navy">
                      <Star className="w-3 h-3 fill-gold text-gold" />{pkg.rating}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-navy">{pkg.name}</h3>
                    <p className="font-body text-xs text-gray-500 mt-1">{pkg.destination} &bull; {pkg.duration}</p>
                    <p className="font-body text-sm text-gray-500 mt-2 line-clamp-2">{pkg.description}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <p className="font-heading text-xl font-bold text-gold">Rs.{pkg.price.toLocaleString('en-IN')}</p>
                      <div className="flex gap-2">
                        <button onClick={() => addToCart(pkg.id)} data-testid={`explore-add-cart-${pkg.id}`}
                          className="bg-gold text-white rounded-full px-4 py-2 text-xs font-body font-medium hover:bg-gold-dark transition-all">
                          Add to Cart
                        </button>
                        <Link to={`/packages/${pkg.id}`} className="bg-navy text-white rounded-full px-4 py-2 text-xs font-body font-medium hover:bg-navy-light transition-all">
                          Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
